import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';
import type { ViewportEngine } from '../../viewer/ViewportEngine';
import { boundsOf, dragged, within } from '../../viewer/window/windowing';
import type { WindowLevel } from './useWindowLevel';

/**
 * The window/level drag, over the image while the toolbar's tool is armed, as
 * in any viewer: press and move — right widens the window, left narrows it;
 * down darkens, up brightens (window/windowing.ts dragged). It takes the press
 * the map would take for a pan; the wheel still zooms. The window box
 * (WindowBox) shows the window as it goes.
 */
export function WindowDrag({ level, engine }: { level: WindowLevel; engine: ViewportEngine }) {
  const ref = useRef<HTMLDivElement>(null);
  // The drag in progress, which outlives the press on the overlay: put down
  // with it if the tool is (Esc, W) before the button comes up.
  const endDrag = useRef<(() => void) | null>(null);
  useEffect(() => () => endDrag.current?.(), []);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    e.preventDefault();
    const start = level.window;
    // Within the image's own range, as the box's drags are (boundsOf).
    const bounds = boundsOf(level.windowing, start);
    const x0 = e.clientX;
    const y0 = e.clientY;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // capture unsupported: the window listeners still follow the drag
    }
    const move = (ev: PointerEvent) => level.set(within(dragged(start, ev.clientX - x0, ev.clientY - y0), bounds));
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      endDrag.current = null;
    };
    endDrag.current?.();
    endDrag.current = end;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };
  // A plain scroll zooms as it does on the image itself (Viewport); a pinch is the page's.
  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    if (e.ctrlKey) return;
    engine.scrollZoom(e.nativeEvent, { x: e.clientX, y: e.clientY });
  };
  return (
    <div
      ref={ref}
      data-window-drag
      aria-label="Drag to set the window and level"
      className="absolute inset-0 z-[4] cursor-move touch-none select-none"
      onPointerDown={onPointerDown}
      onWheel={onWheel}
    />
  );
}
